import { Formula } from '@core/api/models';
import { Product } from '@app/core/hera-models';
import { OrderFormula, OrderProduct } from './order.interfaces';
import { MenuOrderEnum } from '@shared/enum/menu-order';

export class Order {
  productsMap: Map<string, OrderProduct> = new Map();
  formulasMap: Map<string, Array<OrderFormula>> = new Map();

  comment = '';
  sessionId = '';

  get size(): number {
    return [...this.productsMap.values()].reduce(
      (prev, next) => prev + next.quantity,
      0
    );
  }

  get sizeFormula(): number {
    return [...this.formulasMap.values()].reduce(
      (prev, next) => prev + next.length,
      0
    );
  }

  get productsInOrder(): OrderProduct[] {
    return Array.from(this.productsMap.values());
  }

  existingSideProducts(): OrderProduct[] {
    return this.productsInOrder.filter(
      (product) => product.product.category?.code === MenuOrderEnum.SIDE
    );
  }

  existingDishProducts(): OrderProduct[] {
    return this.productsInOrder.filter(
      (product) => product.product.category?.code === MenuOrderEnum.DISH
    );
  }

  get totalPrice(): number {
    return [...this.productsMap.values()].reduce(
      (prev, next) =>
        prev + (next.product.price_incl_vat as number) * next.quantity,
      0
    );
  }

  reset(): void {
    this.productsMap = new Map();
  }

  decreaseFormula(formula: Formula): void {
    const cartFormulas = this.formulasMap.get(formula.id) as OrderFormula[];
    if (cartFormulas?.length > 0) {
      cartFormulas.splice(0, 1);
    } else {
      this.formulasMap.delete(formula.id);
    }
  }

  increaseFormula(formula: Formula): void {
    let cartFormulas =
      (this.formulasMap.get(formula.id) as OrderFormula[]) ?? [];
    if (!cartFormulas) {
      const cartFormula = {
        formulas: [formula],
      };
      cartFormulas = [cartFormula];
    }
    const cartFormula = {
      formulas: [formula],
    };
    cartFormulas.push(cartFormula);
    this.formulasMap.set(formula.id, cartFormulas);
  }

  addProduct(product: Product): void {
    let cartProduct = this.productsMap.get(product.id as string);
    if (!cartProduct) {
      cartProduct = {
        product,
        quantity: 0,
        comment: '',
        expanded: false,
      };
    }

    cartProduct.quantity++;
    this.productsMap.set(product.id as string, cartProduct);
  }

  updateOrderProduct(orderProduct: OrderProduct): void {
    this.productsMap.set(orderProduct.product.id as string, orderProduct);
  }

  async decrementProduct(product: Product): Promise<void> {
    return new Promise<void>(() => {
      const cartProduct = this.productsMap.get(product.id as string);
      if (cartProduct) {
        if (cartProduct.quantity > 0) {
          if (cartProduct.quantity <= 1) {
            this.productsMap.delete(product.id as string);
          } else {
            cartProduct.quantity--;
          }
        }
      }
    });
  }

  getProductQuantity({ id }: Product): number {
    return this.productsMap.get(id as string)?.quantity ?? 0;
  }

  getFormulaQuantity({ id }: Formula): number {
    return this.formulasMap.get(id)
      ? (this.formulasMap.get(id)?.length as number)
      : 0;
  }

  removeProduct(product: Product): void {
    const cartProduct = this.productsMap.get(
      product.id as string
    ) as OrderProduct;
    if (cartProduct?.quantity > 0) {
      this.productsMap.delete(cartProduct.product.id as string);
    }
  }
}
